import React, { memo } from 'react'
import { LOAD_STATE, STOP_TYPE } from 'apps/fleet-planner/constants/loads'
import { obscurePostDeliveryStatus } from 'apps/fleet-planner/pages/loads/utils'
import { IStop } from 'apps/fleet-planner/types'
import {
  IActivity,
  IActivityCheckInTimeUpdated,
  IActivityCheckOutTimeUpdated,
  IActivityShared,
  IActivityShipmentCreated,
  IActivityShipmentImported,
  IActivityStatusUpdated,
  isActivityCheckInTimeUpdated,
  isActivityCheckOutTimeUpdated,
  isActivityShared,
  isActivityShipmentCreated,
  isActivityShipmentImported,
  isActivityStatusUpdated,
} from 'apps/fleet-planner/types/activity'
import {
  getCurrentStopData,
  getStopNumber,
  isMultiStop,
} from 'apps/fleet-planner/utils/driverStatuses'
import { capitalize } from 'utils/capitalize'

interface IActivityTextProps {
  activity: IActivity
  loadStops?: IStop[]
  isExternal?: boolean
}

export const ActivityTextItem: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <span className='load-details-activity-text'>{children}</span>
)

export const getStopName = (
  ifMultiStop: boolean = false,
  stopType?: STOP_TYPE | string,
  stopNumber: string | number = '1'
): string => {
  return ifMultiStop ? `${capitalize(stopType).charAt(0)}${stopNumber}` : ` ${capitalize(stopType)}`
}

/**
 * Return a string with the stop name
 * if multi-stop, the name will be P1, D1, P2, etc.
 * if single-stop, the name will be either Pickup, Dropoff
 */
export const getCurrentStopName = (stopId?: string, loadStops: IStop[] = []): string => {
  if (!stopId) return ''
  const currentStop = loadStops.find(s => s.id === stopId)
  const { relativeStopSequence, stopType } = currentStop || {}
  const ifMultiStop = isMultiStop(currentStop, loadStops)
  return getStopName(ifMultiStop, stopType, relativeStopSequence)
}

/*
 * Return a string with the stop name
 * if multi-stop, the name will be P1, D1, P2, etc.
 * if single-stop, the name will be either Pickup, Dropoff
 * We use currentStopSequence to find the associated stop name in the case of a Status update
 */
export const getStatusCurrentStopName = (
  stopSequence?: number,
  loadStops: IStop[] = []
): string => {
  if (!stopSequence) return ''
  const { currentStopType, isMultiDropoff, isMultiPickup } = getCurrentStopData(
    loadStops,
    stopSequence
  )
  const ifMultiStop = currentStopType === STOP_TYPE.pickup ? isMultiPickup : isMultiDropoff
  const stopNumber = getStopNumber(ifMultiStop, stopSequence, loadStops).replaceAll(' ', '')

  return getStopName(ifMultiStop, currentStopType, stopNumber)
}

/**
 * Returns a shipment created string,
 * i.e. 'Bob Anderson created this load' etc.
 * If there's not actor name, display 'Load created'
 */
export const ActivityTextShipmentCreated: React.FC<{ activity: IActivityShipmentCreated }> = ({
  activity,
}) => {
  const { actorFullName } = activity || {}
  const createdText = actorFullName ? 'created this load' : 'Load created'
  return (
    <ActivityTextItem>
      <span className='b'>{actorFullName || ''}</span> {createdText}
    </ActivityTextItem>
  )
}

/**
 * Returns a shipment imported string,
 * i.e. 'Load imported from Transfix' etc.
 */
export const ActivityTextShipmentImported: React.FC<{ activity: IActivityShipmentImported }> = ({
  activity,
}) => {
  const { metadata } = activity || {}
  const { source } = metadata || {}
  return (
    <ActivityTextItem>
      <span className='b'>Load imported</span> from <span className='b'>{source}</span>
    </ActivityTextItem>
  )
}

/**
 * Returns a shipment shared string,
 * i.e. 'Bob Anderson shared the load with Juan Chavez' etc.
 */
export const ActivityTextShared: React.FC<{ activity: IActivityShared }> = ({ activity }) => {
  const { actorFullName, metadata } = activity || {}
  const { sharedWithDriverName } = metadata || {}
  return (
    <ActivityTextItem>
      <span className='b'>{actorFullName || ''}</span> shared this load with
      <span className='b'> {sharedWithDriverName}</span>
    </ActivityTextItem>
  )
}

/**
 * Returns a check-in time updated string,
 * i.e. 'Bob Anderson updated the Check-in time for Delivery' etc.
 */
export const ActivityTextCheckInTimeUpdated: React.FC<{
  activity: IActivityCheckInTimeUpdated
  loadStops: IStop[]
}> = ({ activity, loadStops = [] }) => {
  const { actorFullName, metadata } = activity || {}
  const { stop } = metadata || {}
  const stopName = getCurrentStopName(stop?.id, loadStops)

  return (
    <ActivityTextItem>
      <span className='b'>{actorFullName}</span>
      {stop.checkedInAt ? ' edited' : ' removed'} the{' '}
      <span className='b'>{`Check-in time for ${stopName}`}</span>
    </ActivityTextItem>
  )
}

/**
 * Returns a check-out time updated string,
 * i.e. 'Bob Anderson updated the Check-out time for D2' etc.
 */
export const ActivityTextCheckOutTimeUpdated: React.FC<{
  activity: IActivityCheckOutTimeUpdated
  loadStops: IStop[]
}> = ({ activity, loadStops = [] }) => {
  const { actorFullName, metadata } = activity || {}
  const { stop } = metadata || {}
  const stopName = getCurrentStopName(stop?.id, loadStops)

  return (
    <ActivityTextItem>
      <span className='b'>{actorFullName}</span> {stop.checkedOutAt ? 'edited' : 'removed'} the{' '}
      <span className='b'>{`Check-out time for ${stopName}`}</span>
    </ActivityTextItem>
  )
}

/**
 * Returns a status updated string,
 * i.e. 'Bob Anderson updated the load status to Delivered' etc.
 */
export const ActivityTextStatusUpdated: React.FC<{
  activity: IActivityStatusUpdated
  loadStops: IStop[]
  isExternal?: boolean
}> = ({ activity, loadStops = [], isExternal }) => {
  const { actorFullName, metadata } = activity || {}
  const { currentStopSequence = 1, status: loadStatus } = metadata || {}

  const status = obscurePostDeliveryStatus(loadStatus, isExternal)

  const formattedStatus = capitalize(status.replaceAll('_', ' '))
  const hasTransitStatus = (status as LOAD_STATE) === LOAD_STATE['In Transit']
  const hasDropoffStatus = (status as LOAD_STATE) === LOAD_STATE['At Dropoff']
  const hasPickupStatus = (status as LOAD_STATE) === LOAD_STATE['At Pickup']
  const stopName = getStatusCurrentStopName(currentStopSequence, loadStops)

  const getLoadStatusText = () => {
    // only In Transit and At Dropoff/Pickup have a different text format from the others,
    // so we explicitly check for those
    if (hasTransitStatus) {
      return `${formattedStatus} to ${stopName}`
    }
    if (hasDropoffStatus || hasPickupStatus) {
      return `At ${stopName}`
    }
    return stopName ? `${formattedStatus} at ${stopName}` : formattedStatus
  }

  return (
    <ActivityTextItem>
      <span className='b'>{actorFullName}</span> updated the load status to{' '}
      <span className='b'>{getLoadStatusText()}</span>
    </ActivityTextItem>
  )
}

export const ActivityText = memo<IActivityTextProps>(({ activity, loadStops = [], isExternal }) => {
  if (isActivityShipmentCreated(activity)) {
    return <ActivityTextShipmentCreated activity={activity} />
  }

  if (isActivityShipmentImported(activity)) {
    return <ActivityTextShipmentImported activity={activity} />
  }

  if (isActivityCheckInTimeUpdated(activity)) {
    return <ActivityTextCheckInTimeUpdated activity={activity} loadStops={loadStops} />
  }

  if (isActivityCheckOutTimeUpdated(activity)) {
    return <ActivityTextCheckOutTimeUpdated activity={activity} loadStops={loadStops} />
  }

  if (isActivityShared(activity)) {
    return <ActivityTextShared activity={activity} />
  }
  if (isActivityStatusUpdated(activity)) {
    return (
      <ActivityTextStatusUpdated
        activity={activity}
        loadStops={loadStops}
        isExternal={isExternal}
      />
    )
  }
  // if none of these cases are true, return blank
  return <></>
})
